import { Component, OnDestroy, OnInit } from "@angular/core";
import { NgZone } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { firstValueFrom, Subscription } from "rxjs";
import RouteNameConstants from "src/app/constants/route-name-constants";
import { Region } from "src/app/models/Region";
import { AnnouncementService } from "src/app/services/announcement.service";
import { AuthService } from "src/app/services/auth.service";
import { CompanyService } from "src/app/services/company.service";
import { RegionService } from "src/app/services/region.service";

@Component({
  selector: "region-select",
  templateUrl: "./region-select.component.html",
  styleUrls: ["./region-select.component.scss"],
  standalone: false,
})
export class RegionSelectComponent implements OnInit, OnDestroy {
  faSpinner = faSpinner;
  isLoading: boolean;
  regions: Region[];
  regionForm: UntypedFormGroup;
  private _subscriptions$: Subscription[] = [];

  constructor(
    private authService: AuthService,
    private regionService: RegionService,
    private fb: UntypedFormBuilder,
    private router: Router,
    private zone: NgZone,
    private companyService: CompanyService,
    private announcementService: AnnouncementService,
  ) {}

  async ngOnInit() {
    this.regions = await this.regionService.getAll().toPromise();
    this.regionForm = this.fb.group({
      region: this.regions.find((r) => r.isCurrentRegion)?.region,
    });

    this.onRegionChange();
  }

  private onRegionChange(): void {
    const subscription = this.region.valueChanges.subscribe((key) => {
      const originalRegion = this.regionService.getCurrentRegion();

      if (
        !originalRegion ||
        originalRegion.region !== key ||
        !this.companyService.selectedCompany
      ) {
        this.changeRegion(key);
      }
    });
    this._subscriptions$.push(subscription);
  }

  private async changeRegion(region: string): Promise<void> {
    this.isLoading = true;
    try {
      const regionDto: Region = { region: region, isCurrentRegion: true };
      const user = await firstValueFrom(this.regionService.set(regionDto));
      this.companyService.unselectCompany();
      this.regionService.setRegionToStorage(region);
      this.authService.onUserProfileRetrieve(user);
      await this.announcementService.retrieveAnnouncementAsync();
    } catch (error) {
      this.setOriginalRegion();
      throw error;
    } finally {
      this.isLoading = false;
      this.navigateToHomepage();
    }
  }

  private setOriginalRegion() {
    const originalRegion = this.regionService.getCurrentRegion();
    if (originalRegion) {
      this.region.setValue(originalRegion.region);
    }
  }

  navigateToHomepage() {
    // navigate to home, if it's not already on home page
    if (this.router.url !== RouteNameConstants.ROOT) {
      this.zone.run(() => this.router.navigate([RouteNameConstants.ROOT]));
    }
  }

  get region() {
    return this.regionForm.get("region");
  }

  ngOnDestroy() {
    this._subscriptions$.forEach((a) => a.unsubscribe());
  }
}
